import React, { useEffect, useRef } from 'react';
import { Card } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Chart } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register the datalabels plugin
Chart.register(ChartDataLabels);

const CustomerBreakdownChart = ({ data, title = "Customer Breakdown" }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    // Clean up any existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // If no data is available, don't create the chart
    if (!data || !Array.isArray(data) || data.length === 0) {
      return;
    }

    // Format data for the chart
    const labels = data.map(item => item.name);
    const values = data.map(item => item.value);
    const colors = data.map(item => item.color || `#${Math.floor(Math.random()*16777215).toString(16)}`);

    // Create the chart
    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: labels,
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderColor: colors.map(() => '#ffffff'),
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 12
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.label || '';
                const value = context.raw || 0;
                const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                const percentage = Math.round((value / total) * 100);
                return `${label}: ${value} (${percentage}%)`;
              }
            }
          },
          datalabels: {
            formatter: (value, ctx) => {
              const sum = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
              const percentage = Math.round((value / sum) * 100);
              return percentage > 5 ? `${percentage}%` : '';
            },
            color: '#ffffff',
            font: {
              weight: 'bold'
            }
          }
        }
      }
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data]);

  // Handle case when data is not available
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Card title={title} className="dashboard-card">
        <div className="card-content">
          <p>No data available</p>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      title={
        <div className="card-title">
          <UserOutlined /> {title}
        </div>
      } 
      className="dashboard-card"
    >
      <div className="card-content" style={{ height: 300 }}>
        <canvas ref={chartRef} />
      </div>
    </Card>
  );
};

export default CustomerBreakdownChart;
